import {
  Button,
  theme as defaultTheme,
  Icon,
  Switch,
  useSnackbar,
  Tooltip,
  TextField,
} from "@mindlercare/ui-web";
import { useFlag } from "@mindlercare/unleash-client";
import { FeedbackLockReason, SequentialUnlockItem } from "api/types";
import {
  useDeleteIcbtFeedback,
  useGetAllFeedbackDraftsByRequestId,
  useGetIcbtFeedbacks,
  useGetIcbtResponses,
  useGetNextSlotAndFeedback,
  usePutIcbtFeedback,
  useSetIcbtRequestFeedbackDateIgnored,
} from "api/useIcbtQueries";
import { getFeedbackLockTranslationKey } from "../../useIcbtTableData/utils";
import { useDemo } from "contexts/DemoContext";
import { format } from "date-fns";
import { useSelectedPsychologistIdParam } from "hooks/useSelectedPsychologistParam";
import { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { styled } from "styled-components";
import { Link } from "components/link";
import { Typography } from "components/typography";
import { Timestamp } from "./Timestamp";
import { getCountryCode } from "helpers/getCountryCode";
import {
  computeBigramSimilarity,
  SIMILARITY_THRESHOLD,
} from "helpers/textSimilarity";
import { getTemplateName } from "../../../../../../helpers/templates";
import { GenerateDraft } from "./GenerateDraft";
import { SimilarityConfirmModal } from "./SimilarityConfirmModal";
import {
  FormContainer,
  TimestampContainer,
  Placeholder,
  CharCount,
  ActionContainer,
  ButtonWrapper,
  InputContainer,
  ReplyHeader,
} from "./sharedFormStyles";

const NextSlotAndFeedbackContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const NextContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const VerticalDivider = styled.div`
  width: 1px;
  height: 100%;
  background-color: ${defaultTheme.COLORS.GRAY_MEDIUM};
`;

const ReplyContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${defaultTheme.SPACING.MD};
  flex-wrap: wrap;
`;

const PatientNameLink = styled(Link)`
  color: inherit;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const AiSettingsButton = styled(Button)`
  display: flex;
  align-items: center;
  height: auto;
  align-self: flex-end;
  gap: ${defaultTheme.SPACING.XS};
  font-size: ${defaultTheme.FONT.SIZE.SMALL};
  font-weight: ${defaultTheme.FONT.WEIGHT.MEDIUM};
  color: ${defaultTheme.COLORS.GRAY_DARK};
  text-decoration: underline;
  letter-spacing: -0.24px;
`;

const AiDraftDisclaimer = styled(Typography)`
  align-self: flex-end;
  text-align: right;
  letter-spacing: -0.24px;
`;

const PatientInfoHead = styled.p`
  font-weight: ${() => defaultTheme.FONT.WEIGHT.MEDIUM};
`;

const SwitchContainer = styled.div`
  left: 0px;
  display: flex;
  align-items: center;
`;

const TextFieldInfoSpan = styled.div`
  font-size: ${() => defaultTheme.FONT.SIZE.SMALL};
  display: flex;
  flex-direction: row;
  margin-top: -${() => defaultTheme.SPACING.SM};
`;

const MAX_LENGTH = 1500;

interface Props {
  patientName: string;
  userId: number;
  patientSsn: string | null;
  icbtRequestId: string;
  icbtFeedbackDate?: string;
  icbtFeedbackDateIgnored?: boolean;
  programId: string;
  isFeedbackLocked?: boolean;
  feedbackLockReason?: FeedbackLockReason;
  isRequestOwner?: boolean;
  assessment?: SequentialUnlockItem["assessment"];
}

interface InferPlaceholderFromStateProps {
  t: TFunction;
  icbtFeedbackDate?: string;
}
const inferPlaceholderFromState = ({
  t,
  icbtFeedbackDate,
}: InferPlaceholderFromStateProps) => {
  if (!icbtFeedbackDate) {
    return t("FEEDBACK_TEXTAREA_PLACEHOLDER_NO_DATE");
  }
  return t("FEEDBACK_TEXTAREA_PLACEHOLDER");
};

export const FeedbackForm = ({
  patientName,
  userId,
  patientSsn,
  icbtRequestId,
  icbtFeedbackDate,
  icbtFeedbackDateIgnored,
  programId,
  isFeedbackLocked,
  feedbackLockReason,
  isRequestOwner,
}: Props) => {
  const { t } = useTranslation();
  const [feedbackInput, setFeedbackInput] = useState<string>("");
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const { mutateAsync: putFeedback } = usePutIcbtFeedback();
  const { mutateAsync: deleteFeedbackDraft } = useDeleteIcbtFeedback();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedPsychologistId] = useSelectedPsychologistIdParam();
  const isDemo = useDemo();

  const isGenerateLLMFeedbackEnabled = useFlag("ICBT_GENERATE_LLM_FEEDBACK");
  const isCustomPromptsEnabled = useFlag("ICBT_LLM_FEEDBACK_CUSTOM_PROMPTS");
  // Only the treating (requesting) psychologist may generate/fetch drafts.
  const canGenerateDraft =
    isGenerateLLMFeedbackEnabled &&
    isRequestOwner &&
    !!programId &&
    !!icbtFeedbackDate &&
    !icbtFeedbackDateIgnored &&
    !isFeedbackLocked;

  const market = getCountryCode();
  const numericIcbtRequestId = parseInt(icbtRequestId, 10);
  const userIdString = userId.toString();

  const { data: previousDraftsResponse, refetch: refetchPreviousDrafts } =
    useGetAllFeedbackDraftsByRequestId(
      { programId, market, icbtRequestId: numericIcbtRequestId },
      { enabled: canGenerateDraft, refetchOnMount: "always" },
    );

  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showSimilarityConfirm, setShowSimilarityConfirm] = useState(false);
  const [pendingSendStatus, setPendingSendStatus] = useState<
    "draft" | "published" | null
  >(null);
  const [appliedDraftText, setAppliedDraftText] = useState<string>("");

  // Prefer the locally-applied draft text over the refetched response so the
  // similarity guard is not dependent on the refetch resolving before the user
  // clicks Send.
  const latestCompletedLlmDraftText =
    previousDraftsResponse?.drafts.find((draft) => draft.status === "completed")
      ?.draftText ?? "";
  const lastGeneratedDraftText =
    appliedDraftText || latestCompletedLlmDraftText;

  const similarityScore = useMemo(() => {
    if (!lastGeneratedDraftText || !feedbackInput) return null;
    return computeBigramSimilarity(feedbackInput, lastGeneratedDraftText);
  }, [feedbackInput, lastGeneratedDraftText]);

  const fetchDrafts = useCallback(
    async () => (await refetchPreviousDrafts()).data?.drafts ?? [],
    [refetchPreviousDrafts],
  );

  const handleDraftApplied = useCallback((draftText: string) => {
    setFeedbackInput(draftText);
    setAppliedDraftText(draftText);
  }, []);

  const handleRegeneratingChange = useCallback((value: boolean) => {
    setIsRegenerating(value);
  }, []);

  const location = useLocation();
  const isEditingText = useRef(false);

  const { data: nextSlotAndFeedback } = useGetNextSlotAndFeedback(
    String(userId),
  );

  const { slot: nextSlot, icbtRequest: nextIcbtRequest } =
    nextSlotAndFeedback ?? {};

  const { data: feedbacks, isFetching } = useGetIcbtFeedbacks({
    userId,
    icbtRequestId,
  });

  // There should only ever be at maximum one draft feedback for a specific icbtRequestId.
  const draftFeedback = feedbacks?.find(
    (feedback) => feedback.status === "draft",
  );

  const publishedFeedbacks = feedbacks
    ?.filter((feedback) => feedback.status === "published")
    .sort(
      (a, b) =>
        new Date(b.published_at ?? b.updated_at).getTime() -
        new Date(a.published_at ?? a.updated_at).getTime(),
    );

  const { data: notes } = useGetIcbtResponses({
    userId,
    icbtRequestId,
  });

  const isPlaceholderSetup = useRef(false);

  const draftIsUnchanged =
    feedbackInput === draftFeedback?.feedback_text ||
    (!feedbackInput && !draftFeedback?.feedback_text);

  // Prefill the box once: the saved feedback draft wins, otherwise the latest
  // generated AI draft, so a refresh during generation does not lose it. The
  // similarity guard still fires on send because the text matches that draft.
  useEffect(() => {
    if (isPlaceholderSetup.current || feedbackInput || isFetching) return;
    const prefill =
      draftFeedback?.feedback_text ||
      (feedbacks ? latestCompletedLlmDraftText : "");
    if (!prefill) return;
    setFeedbackInput(prefill);
    isPlaceholderSetup.current = true;
  }, [
    draftFeedback,
    feedbacks,
    isFetching,
    latestCompletedLlmDraftText,
    feedbackInput,
    setFeedbackInput,
  ]);

  useEffect(() => {
    isPlaceholderSetup.current = false;
  }, [userId, icbtRequestId]);

  const sendFeedback = async ({
    status,
    skipSimilarityCheck = false,
  }: {
    status: "draft" | "published";
    skipSimilarityCheck?: boolean;
  }) => {
    if (status === "published" && !feedbackInput) return;
    if (status === "draft" && draftIsUnchanged) return;

    if (
      status === "published" &&
      !skipSimilarityCheck &&
      lastGeneratedDraftText &&
      computeBigramSimilarity(feedbackInput, lastGeneratedDraftText) >=
        SIMILARITY_THRESHOLD
    ) {
      setPendingSendStatus(status);
      setShowSimilarityConfirm(true);
      return;
    }

    setIsSendingFeedback(true);

    // Deleting the draft
    if (status === "draft" && feedbackInput === "" && !!draftFeedback) {
      await deleteFeedbackDraft({
        userId,
        feedbackId: draftFeedback.feedback_id,
      });

      enqueueSnackbar({
        text: t("DELETED_FEEDBACK_DRAFT"),
      });
      setIsSendingFeedback(false);
      return;
    }

    // Otherwise we are updating/publishing
    const snackbarText =
      status === "published" ? t("SENT_FEEDBACK") : t("SAVED_FEEDBACK_DRAFT");

    try {
      await putFeedback({
        userId,
        icbtRequestId,
        status,
        feedbackText: feedbackInput,
      });
      enqueueSnackbar({ text: snackbarText });
      if (status === "published") {
        setFeedbackInput("");
      }
    } catch {
      enqueueSnackbar({ text: t("API_ERROR_GENERIC") });
    }
    setIsSendingFeedback(false);
  };

  const handleSimilarityConfirm = useCallback(
    (confirmed: boolean) => {
      if (!confirmed) {
        setShowSimilarityConfirm(false);
        setPendingSendStatus(null);
        return;
      }
      if (pendingSendStatus) {
        sendFeedback({
          status: pendingSendStatus,
          skipSimilarityCheck: true,
        }).then(() => {
          setShowSimilarityConfirm(false);
        });
      }
      setPendingSendStatus(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingSendStatus],
  );

  const pastDueDate =
    icbtFeedbackDate && Date.now() > Date.parse(icbtFeedbackDate);

  const patientHaveReflected = (notes && notes.length > 0) ?? false;
  const shouldLinkPatient = location.pathname === "/icbt-feedback";
  const patientLink = `/patients/psychologist/${selectedPsychologistId}/patient/${userId}/icbt`;

  const [isFeedbackDateIgnored, setIsFeedbackDateIgnored] = useState<boolean>(
    Boolean(icbtFeedbackDateIgnored),
  );

  // If psy didn't leave any feedback yet, this should be undefined or empty.
  const anyExistingFeedback = publishedFeedbacks?.length;

  const sendingFeedbackIsAllowed =
    (patientHaveReflected || anyExistingFeedback || pastDueDate) &&
    !isFeedbackDateIgnored &&
    !isFeedbackLocked &&
    !!feedbackInput;

  const sendFeedbackDisabledReason = t("SEND_FEEDBACK_DISABLED", {
    notPastDueDate: !pastDueDate ? t("SEND_FEEDBACK_BEFORE_DUE_DATE") : "",
    patientHadNotReflected: !patientHaveReflected
      ? t("SEND_FEEDBACK_PATIENT_HAD_NOT_REFLECTED")
      : "",
  });

  const isDraftDisabled =
    !icbtFeedbackDate ||
    draftIsUnchanged ||
    isSendingFeedback ||
    isFeedbackDateIgnored ||
    isFeedbackLocked;
  const noFeedbackNeededToggleDisabled = Boolean(anyExistingFeedback);

  const { mutateAsync: toggleFeedbackDateIgnored } =
    useSetIcbtRequestFeedbackDateIgnored();

  const ignoreFeedbackDateFeatureEnabled = useFlag("NO_ICBT_FEEDBACK_NEEDED");

  return (
    <FormContainer>
      <ReplyContainer>
        <ReplyHeader variant="body">
          {shouldLinkPatient ? (
            <PatientNameLink to={patientLink} variant="body">
              {t("REPLY_TO", { name: patientName })}
            </PatientNameLink>
          ) : (
            <span>{t("REPLY_TO", { name: patientName })}</span>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              marginTop: "4px",
            }}
          >
            <Icon.Profile fill={defaultTheme.COLORS.GRAY} />
            <Typography variant="body-x-small" color="GRAY">
              {`${patientSsn} · UserId: ${userId}`}
            </Typography>
          </div>
        </ReplyHeader>
        {canGenerateDraft && (
          <GenerateDraft
            defaultLanguage="SV"
            programId={programId}
            market={market}
            disabled={isFeedbackDateIgnored}
            icbtRequestId={numericIcbtRequestId}
            patientUserId={userIdString}
            feedbackInput={feedbackInput}
            previousDrafts={previousDraftsResponse?.drafts}
            fetchDrafts={fetchDrafts}
            onNewDraft={handleDraftApplied}
            onRegenerating={handleRegeneratingChange}
          />
        )}
      </ReplyContainer>
      <NextSlotAndFeedbackContainer>
        <NextContainer>
          <Icon.Video fill={defaultTheme.COLORS.GRAY_BLACK} />
          <div>
            <PatientInfoHead>{t("NEXT_SESSION")}</PatientInfoHead>
            {nextSlot ? (
              <Tooltip
                content={t("NEXT_SESSION_TOOLTIP", {
                  slotType: getTemplateName(nextSlot.templateName, t),
                })}
              >
                <span>
                  <Typography variant="body" color="GRAY">
                    {format(Date.parse(nextSlot.startsAt), "eeee, LLL d")}
                  </Typography>
                </span>
              </Tooltip>
            ) : (
              <Typography variant="body" color="GRAY">
                {t("NO_NEXT_SESSION")}
              </Typography>
            )}
          </div>
        </NextContainer>
        <VerticalDivider />
        <NextContainer style={{ color: defaultTheme.COLORS.GRAY_BLACK }}>
          <Icon.Feedback
            fill={defaultTheme.COLORS.GRAY_BLACK}
            style={{
              width: 21,
              height: 21,
            }}
          />
          <div>
            <PatientInfoHead>{t("NEXT_FEEDBACK")}</PatientInfoHead>
            {nextIcbtRequest?.feedbackDate ? (
              <>
                <Typography variant="body">
                  {format(
                    Date.parse(nextIcbtRequest.feedbackDate),
                    "eeee, LLL d",
                  )}
                </Typography>
              </>
            ) : (
              <Typography variant="body" color="GRAY">
                {t("NO_NEXT_FEEDBACK")}
              </Typography>
            )}
          </div>
        </NextContainer>
      </NextSlotAndFeedbackContainer>
      <InputContainer>
        <TextField
          width="full"
          hideSensitiveData={isDemo}
          multiline
          unlimitedRows
          placeholder={
            isFeedbackLocked
              ? t(getFeedbackLockTranslationKey(feedbackLockReason))
              : inferPlaceholderFromState({ t, icbtFeedbackDate })
          }
          maxlength={MAX_LENGTH}
          value={
            isFeedbackDateIgnored
              ? t("FEEDBACK_TEXTAREA_PLACEHOLDER_NO_FEEDBACK")
              : isFeedbackLocked
                ? ""
                : feedbackInput
          }
          onChange={(e) => {
            if (!isEditingText.current) isEditingText.current = true;
            const newValue = e.target.value;
            setFeedbackInput(newValue);
          }}
          // blueText={isFeedbackDateIgnored}
          // loading={isRegenerating}
          disabled={
            !icbtFeedbackDate ||
            isFetching ||
            isFeedbackDateIgnored ||
            isRegenerating ||
            isFeedbackLocked
          }
        />
        {draftFeedback ? (
          <TimestampContainer>
            <Timestamp alignSelf="center" status="edited">
              {draftFeedback.updated_at}
            </Timestamp>
          </TimestampContainer>
        ) : (
          <Placeholder />
        )}
        <CharCount>
          {feedbackInput?.length ?? 0}/{MAX_LENGTH}
        </CharCount>
      </InputContainer>
      {publishedFeedbacks?.length ? (
        <TextFieldInfoSpan>
          <Typography variant="body-x-small" color="GRAY">
            {t("FEEDBACK")}{" "}
          </Typography>
          &nbsp;
          <Timestamp status="sent" lowercased>
            {publishedFeedbacks[0].published_at ??
              publishedFeedbacks[0].updated_at}
          </Timestamp>
        </TextFieldInfoSpan>
      ) : null}
      <ActionContainer>
        <ButtonWrapper>
          <Button
            disabled={isDraftDisabled}
            variant="light"
            onClick={() => sendFeedback({ status: "draft" })}
          >
            {t("SAVE_DRAFT")}
          </Button>
          <Tooltip
            content={sendFeedbackDisabledReason}
            disabled={!!sendingFeedbackIsAllowed || !feedbackInput}
          >
            <Button
              disabled={!sendingFeedbackIsAllowed || isSendingFeedback}
              variant="primary"
              onClick={() => sendFeedback({ status: "published" })}
            >
              {t("SEND_FEEDBACK")}
            </Button>
          </Tooltip>
        </ButtonWrapper>
        {ignoreFeedbackDateFeatureEnabled && (
          <SwitchContainer>
            <Switch
              disabled={noFeedbackNeededToggleDisabled}
              ml={4}
              mt={8}
              isActive={isFeedbackDateIgnored}
              onClick={(event) => {
                const isTogglingOff = isFeedbackDateIgnored;
                event.stopPropagation();
                setIsFeedbackDateIgnored(!isTogglingOff); // optimistic update for UI purposes
                toggleFeedbackDateIgnored({
                  mode: isTogglingOff ? "CEASE_IGNORE" : "IGNORE",
                  icbtRequestId,
                })
                  .then(() => {
                    enqueueSnackbar({
                      text: isTogglingOff
                        ? t("CEASE_IGNORE_ICBT_FEEDBACK_DATE")
                        : t("IGNORED_ICBT_FEEDBACK_DATE"),
                      isError: false,
                    });
                    if (isTogglingOff) {
                      setFeedbackInput("");
                    }
                  })
                  .catch(() => {
                    enqueueSnackbar({
                      text: t("FAILED_SAVE"),
                      isError: true,
                    });
                    setIsFeedbackDateIgnored(isTogglingOff); // reset previous state
                  });
              }}
            />
            <Typography
              variant="body-x-small"
              color="GRAY_DARK"
              sx={{ lineHeight: "unset" }}
            >
              {t("NO_FEEDBACK_NEEDED")}
            </Typography>
          </SwitchContainer>
        )}
      </ActionContainer>
      {isGenerateLLMFeedbackEnabled && isRequestOwner && (
        <>
          <AiDraftDisclaimer variant="body-x-small" color="GRAY">
            {t("LLM_AI_FEEDBACK.AI_DRAFT_DISCLAIMER")}
          </AiDraftDisclaimer>
          {isCustomPromptsEnabled && (
            <AiSettingsButton
              variant="link-button"
              href="/user-settings"
              target="_blank"
            >
              <Icon.Info height={16} width={16} />
              {t("LLM_AI_FEEDBACK.FEEDBACK_SETTINGS_LINK")}
            </AiSettingsButton>
          )}
        </>
      )}
      <SimilarityConfirmModal
        open={showSimilarityConfirm}
        onClose={handleSimilarityConfirm}
        generatedDraftText={lastGeneratedDraftText}
        feedbackInput={feedbackInput}
        similarityScore={similarityScore}
        isSending={isSendingFeedback}
      />
    </FormContainer>
  );
};
